import { reinitFlowbiteWidgets } from '../utils/ui/flowbite-reinit.js';
import { lockButton, unlockButton } from '../utils/forms/submit-button.js';
import { createDynamicModal } from '../utils/ui/dynamic-modal.js';
import { getCsrfToken } from '../utils/csrf.js';

/**
 * CODA upload — Billing / Bank statements
 *
 * Centered-modal single-file upload for the CODA bank-statement list.
 * The form is tiny (bank account + file) and the action is fire-and-
 * forget, so a modal is the right affordance. Drawers in this app are
 * reserved for richer contextual workspaces (e.g. the P5b CODA line
 * drawer, which carries match suggestions + payment history).
 *
 * The modal itself follows the canonical "static shell + dynamic
 * content (vanilla fetch AJAX)" pattern — see ``form-modals.mdc`` and
 * ``utils/ui/dynamic-modal.js``. The shell lives in
 * ``_upload_modal_shell.html`` (header + .modal-content target only);
 * the form is fetched on every open and replaced with bound errors on
 * any 422 round-trip, so reopening can never resurface stale state.
 *
 * Success path — ``createDynamicModal`` receives a JSON
 * ``{success: true}`` response; our ``onSuccess`` hook then takes
 * care of the list refresh + poll-loop kick. We refresh
 * ``#coda-list`` ourselves (rather than letting the server return the
 * fragment) because the list refresh has to happen *after* the modal
 * is dismissed, and because keeping the wrapper node identity is
 * important for the polling loop (see ``pollTick`` below).
 *
 * Polling (parsing → ready flip):
 *
 *   The list fragment used to rely on HTMX's `hx-trigger="every 3s"`
 *   to repaint itself while any row was in PARSING state. In practice
 *   that mechanism proved unreliable after the JS-driven outerHTML
 *   swap: HTMX's internal trigger registry isn't re-armed for the
 *   freshly-parsed node, so the 3-second timer never fires and the
 *   status badge sticks on "Parsing" until a full page refresh. We
 *   tried `htmx.process()` and `htmx.ajax()` refetches — neither
 *   landed a single poll request in the server log. The fix is to own
 *   the poll loop ourselves: a plain `setInterval` that fetches the
 *   fragment URL, swaps `#coda-list`, and stops once no row is in
 *   PARSING anymore. Zero HTMX dependency, guaranteed to work.
 */

function findClosest(el, selector) {
    var node = (el && el.nodeType === 1) ? el : (el && el.parentElement) || null;
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest(selector);
}

// NOTE — no toasts here on purpose.
//   * Success toast ("uploaded — parsing in background") is redundant: the
//     new row with its Parsing / Ready / Parse failed status badge is the
//     canonical feedback surface.
//   * Validation-error toast is redundant: Django re-renders the modal
//     partial (HTTP 422) with inline field errors right next to the
//     offending control, which is where the user is looking.
//   * Parse-error feedback lives in the row's status badge + tooltip + audit
//     log — parse errors come from Celery, long after this fetch returns.
//   * Infra failures (4xx/5xx, network down) are rare; the modal stays open
//     and the submit button re-enables, so the user can just retry.

// Re-run global widget upgrades on freshly-injected form HTML.
// Currently just ``initCustomSelects``: the bank-account <select> carries
// ``class="form-select"`` so the custom-select module converts it to the
// same styled dropdown used by every other office form (consumable,
// invoice, quote, ...). ``initCustomSelects`` is idempotent — it tears
// down any existing wrappers before recreating them, so calling it after
// every inject is safe.
function upgradeInjectedWidgets() {
    if (typeof window.initCustomSelects === 'function') {
        window.initCustomSelects();
    }
}

// ---------------------------------------------------------------------------
// Polling
// ---------------------------------------------------------------------------
// A single long-lived interval per page. We store the handle on `window` so
// multiple init passes (HTMX boosted nav, manual reload of this module) can
// cancel the previous loop instead of stacking them.

var POLL_INTERVAL_MS = 3000;

function syncDataAttrs(target, source) {
    // Keep the wrapper node identity (avoids flicker — see pollTick)
    // but make sure every server-driven data-* attribute on the FRESH
    // wrapper is reflected on the existing one. Without this,
    // `data-poll-needed` (the single signal the poll loop reads) is
    // stuck at its page-load value and the JS never notices that the
    // server has new work to observe — resulting in the exact bug we
    // just fixed: upload a CODA file, status badge stays on
    // "Parsing", user has to F5 to see the final match counts.
    //
    // We also clean up attributes that disappeared server-side, to
    // keep the two sides in sync both ways (e.g. when polling should
    // STOP the server drops `data-poll-needed` — we must remove it
    // too so the next tick stops the interval).
    if (!target || !source) return;
    var i;
    var seen = {};
    for (i = 0; i < source.attributes.length; i++) {
        var a = source.attributes[i];
        if (a.name.indexOf('data-') !== 0) continue;
        seen[a.name] = true;
        if (target.getAttribute(a.name) !== a.value) {
            target.setAttribute(a.name, a.value);
        }
    }
    var targetAttrs = Array.prototype.slice.call(target.attributes);
    for (i = 0; i < targetAttrs.length; i++) {
        var name = targetAttrs[i].name;
        if (name.indexOf('data-') !== 0) continue;
        if (!seen[name]) target.removeAttribute(name);
    }
}

function needsPolling(listEl) {
    // Single source of truth: the server stamps `data-poll-needed="1"`
    // on `#coda-list` whenever there's still observable server-side
    // work going on. Two cases qualify:
    //
    //   1. At least one import is still `status=PARSING` (Celery's
    //      parser hasn't finished).
    //   2. At least one READY import has lines without a
    //      PaymentMatchLog yet — i.e. the chained matcher task hasn't
    //      produced a decision for every line. Without polling this
    //      phase, the match-summary badges would freeze on "all
    //      unmatched" for the few hundred ms between parse-done and
    //      match-done, and the user would have to hit F5 to see the
    //      real counts. (This was the observed bug before the view
    //      started emitting this attribute — see
    //      `apps/office/views/billing_coda.py::_needs_polling`.)
    //
    // The view computes both conditions server-side and collapses
    // them into this single attribute, so the JS has zero business
    // logic to duplicate.
    return !!(listEl && listEl.getAttribute('data-poll-needed') === '1');
}

function stopCodaPolling() {
    if (window.__codaPollHandle) {
        clearInterval(window.__codaPollHandle);
        window.__codaPollHandle = null;
    }
}

async function refreshCodaList() {
    // Refetch and swap #coda-list in place (preserving wrapper identity to
    // avoid flicker). Used both by the poll loop and by the post-upload
    // success path. Returns silently on any failure — the next poll tick
    // (if armed) will try again, and a stale-but-rendered list is
    // strictly better than a JS exception that breaks subsequent ticks.
    var listEl = document.getElementById('coda-list');
    if (!listEl) return;

    var fragmentUrl = listEl.getAttribute('data-list-fragment-url');
    if (!fragmentUrl) return;

    try {
        var response = await fetch(fragmentUrl, {
            method: 'GET',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
        });
        if (!response.ok) return;

        var html = await response.text();
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newList = doc.getElementById('coda-list');
        if (!newList) return;

        // Smooth swap: keep the wrapper <div#coda-list> in place (so the
        // browser doesn't unpaint / re-create the scrolling container on
        // every tick, which caused a visible flicker) and only swap its
        // inner HTML. `replaceWith()` was the naive choice and it worked
        // functionally, but tearing down the entire wrapper 1×/3s made
        // the status-badge transition feel like a mini page reload.
        //
        // We preserve the wrapper node identity for zero-flicker,
        // but we DO need to sync the server-driven data-* attributes
        // on it — `data-poll-needed` in particular flips True→False
        // when all matchers finish, and if we don't copy it the poll
        // loop would keep ticking forever.
        syncDataAttrs(listEl, newList);
        listEl.innerHTML = newList.innerHTML;

        // Re-wire two separate things on the freshly-injected rows:
        //
        //   1. Flowbite widgets (re-parse tooltip, parse-failed error
        //      tooltip). innerHTML gives us bare DOM, not the
        //      initialised Popper/Tooltip instances.
        //   2. HTMX attributes on the re-parse forms (`hx-post`,
        //      `hx-target`, ...). Without `htmx.process()` the form
        //      still has a regular `method="post" action="..."`, so
        //      clicking "Re-parse" triggers a NATIVE form submit and
        //      the browser navigates to the bare fragment URL — no
        //      base template, no CSS, a completely unstyled table
        //      (the "naked table" bug). `htmx.process()` is
        //      scoped — it walks `listEl` and arms any hx-* it
        //      finds; it does NOT re-process the whole page, so
        //      it's safe to call every tick.
        //
        //      NOTE: `window.htmx` is exposed by `app.js` (the ESM
        //      build doesn't self-assign to window). If you ever
        //      see the naked-table bug regress, the first thing to
        //      check is that the global is still being set.
        reinitFlowbiteWidgets(listEl);
        if (window.htmx) {
            window.htmx.process(listEl);
        }
    } catch (err) {
        console.error('[coda-upload] list refresh failed', err);
    }
}

async function pollTick() {
    var listEl = document.getElementById('coda-list');
    if (!listEl) { stopCodaPolling(); return; }

    await refreshCodaList();

    // If the server says nothing is pending anymore, we're done — save cycles.
    if (!needsPolling(listEl)) {
        stopCodaPolling();
    }
}

function ensureCodaPolling() {
    var listEl = document.getElementById('coda-list');
    if (!listEl) return;
    if (!needsPolling(listEl)) return;
    if (window.__codaPollHandle) return;

    // Fire an IMMEDIATE tick (well, on the next macrotask so this
    // function returns first and the caller can finish any DOM work).
    // Without this the user would have to wait for the full
    // POLL_INTERVAL_MS before the *first* status refresh — and since
    // Celery finishes in a few dozen ms, that 3-second empty wait is
    // the single most visible latency in the upload flow.
    setTimeout(pollTick, 0);

    // Then keep ticking at the regular cadence until pollTick() calls
    // stopCodaPolling() on a response with no parsing rows.
    window.__codaPollHandle = setInterval(pollTick, POLL_INTERVAL_MS);
}

// ---------------------------------------------------------------------------
// Upload modal — uses the canonical dynamic-modal helper.
// ---------------------------------------------------------------------------
// The shell is in `_upload_modal_shell.html` (id="coda-upload-modal" with
// .modal-content as the dynamic target). We keep a single helper instance
// per page; multiple `data-coda-open` triggers share it via `helper.open()`.

let codaUploadModal = null;

function ensureCodaUploadModal() {
    // Reuse the cached helper ONLY while the modal DOM node it captured is
    // still in the live document. The shell (#coda-upload-modal) lives
    // inside #main-content (see coda/list.html), so every hx-boost
    // navigation away-and-back replaces it with a fresh node. The helper
    // captured the OLD node's references at build time (modal / content /
    // panel / close-btn — see createDynamicModal); once that node is
    // detached, open() would toggle .hidden on an orphan and nothing
    // appears. ``isConnected`` detects the swap so we rebuild against the
    // fresh node. (Rebuilding is safe: the Escape listener is bound once
    // globally via bindEscapeOnce, not per helper.)
    if (codaUploadModal && codaUploadModal.modal && codaUploadModal.modal.isConnected) {
        return codaUploadModal;
    }
    codaUploadModal = null;
    var root = document.querySelector('[data-auto-init="codaUpload"]');
    var errorMsg = (root && root.getAttribute('data-error-load-form'))
        || 'Could not load the form. Please close and try again.';

    codaUploadModal = createDynamicModal({
        modalId: 'coda-upload-modal',
        // Re-init custom-select on every content swap (initial GET and
        // any 422 re-render). Idempotent so this is safe to call repeatedly.
        onContentInjected: () => {
            upgradeInjectedWidgets();
        },
        // The dynamic-modal helper drives a generic spinner via
        // ``lockButton`` already, but the upload button has a
        // contextual "Uploading…" label that's nicer to read while a
        // multi-megabyte file is moving across the wire. We swap to
        // it here and let lockButton finish its job once the response
        // arrives (no need to call unlockButton — onSuccess closes
        // the modal and tears down the DOM, and on 422 the helper
        // re-renders the partial with a fresh button).
        beforeSubmit: (form) => {
            var submitBtn = form.querySelector('button[type="submit"]');
            if (!submitBtn) return;
            var label = submitBtn.getAttribute('data-uploading-label');
            if (label) {
                lockButton(submitBtn, { label });
                // lockButton was already going to fire from the helper
                // right after this — calling it now means our label
                // wins over the generic "Loading…" default, and the
                // helper's second call is a harmless no-op on an
                // already-locked button.
            }
        },
        onSuccess: (_data, helper) => {
            // Close the modal first so the new row appears in a "clean"
            // viewport (no overlay), then refresh + kick polling. The
            // poll loop has its own retry on transient errors, so a
            // failed refreshCodaList() here just means the user sees the
            // freshly-PARSING row a few seconds later — never broken.
            helper.close();
            refreshCodaList().then(ensureCodaPolling);
        },
        errorLoadingMessage: errorMsg,
    });
    return codaUploadModal;
}

// ---------------------------------------------------------------------------
// Bulk-archive selection
// ---------------------------------------------------------------------------
// The checkbox column + floating bulk bar are rendered server-side. All this
// JS does is:
//
//   * keep the bulk-bar visibility in sync with the current selection
//     (at least one checked row → show),
//   * keep the "select-all" checkbox in the <thead> in a sensible state
//     (checked when every archivable row is checked, indeterminate when
//     some are, unchecked when none),
//   * POST `ids[]` to the bulk-archive endpoint and swap #coda-list with
//     the response, preserving the user's current archived filter so the
//     refresh doesn't flip them from "All" back to "Active".
//
// The list fragment is re-rendered by the server on every action (archive,
// reparse, delete, bulk archive, polling), so checkbox state is
// intentionally ephemeral — refreshing the fragment clears the selection.
// That matches the mental model: once you submit, the action is done.

function getBulkBar() { return document.getElementById('coda-bulk-bar'); }
function getBulkCount() { return document.getElementById('coda-bulk-count'); }
function getSelectAll() { return document.getElementById('coda-select-all'); }
function getRowChecks() {
    return document.querySelectorAll('#coda-list .coda-row-check');
}
function getCheckedRows() {
    return document.querySelectorAll('#coda-list .coda-row-check:checked');
}

function updateBulkBar() {
    var bar = getBulkBar();
    var count = getBulkCount();
    var checked = getCheckedRows();
    var n = checked.length;
    if (count) count.textContent = String(n);
    if (bar) {
        if (n > 0) bar.classList.remove('hidden');
        else bar.classList.add('hidden');
    }
    var all = getSelectAll();
    if (all) {
        var rows = getRowChecks();
        if (rows.length === 0 || n === 0) {
            all.checked = false;
            all.indeterminate = false;
        } else if (n === rows.length) {
            all.checked = true;
            all.indeterminate = false;
        } else {
            all.checked = false;
            all.indeterminate = true;
        }
    }
}

function clearBulkSelection() {
    var rows = getRowChecks();
    for (var i = 0; i < rows.length; i++) rows[i].checked = false;
    updateBulkBar();
}

async function submitBulkArchive() {
    var root = document.querySelector('[data-auto-init="codaUpload"]');
    if (!root) return;
    var url = root.getAttribute('data-archive-bulk-url');
    if (!url) return;
    var checked = getCheckedRows();
    if (checked.length === 0) return;

    var form = new FormData();
    for (var i = 0; i < checked.length; i++) form.append('ids', checked[i].value);
    // Preserve the current filter on the refreshed fragment, otherwise
    // bulk-archiving from the "All" view would silently bump the user
    // back to "Active" on the response render.
    var mode = root.getAttribute('data-archived-mode') || 'active';
    form.append('archived', mode);

    var btn = document.getElementById('coda-bulk-archive');
    // The canonical `lockButton` spinner uses `currentColor`, so it
    // inherits the button's text colour automatically — works on both
    // `btn-primary` (white text) and `btn-secondary` (coloured text).
    if (btn) {
        var archivingLabel = btn.getAttribute('data-archiving-label') || 'Archiving…';
        lockButton(btn, { label: archivingLabel });
    }
    try {
        var resp = await fetch(url, {
            method: 'POST',
            body: form,
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRFToken': getCsrfToken() || '',
            },
            credentials: 'same-origin',
        });
        if (!resp.ok) return;
        var html = await resp.text();
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newList = doc.getElementById('coda-list');
        var listEl = document.getElementById('coda-list');
        if (newList && listEl) {
            syncDataAttrs(listEl, newList);
            listEl.innerHTML = newList.innerHTML;
            reinitFlowbiteWidgets(listEl);
            if (window.htmx) window.htmx.process(listEl);
        }
        updateBulkBar();
        ensureCodaPolling();
    } catch (err) {
        console.error('[coda-upload] bulk archive failed', err);
    } finally {
        if (btn) unlockButton(btn);
    }
}

export function initCodaUpload() {
    var root = document.querySelector('[data-auto-init="codaUpload"]');
    if (!root) return;

    // Cancel any previous loop (happens on HTMX-boosted navigation
    // when this module is re-initialised) and re-arm based on the
    // current DOM state.
    stopCodaPolling();
    ensureCodaPolling();

    // Lazily build the modal helper. Doing this once at init time
    // (rather than on every click) keeps the Escape / close-button
    // listeners stable and avoids duplicate handlers on re-init.
    ensureCodaUploadModal();

    // Checkbox + bulk bar wiring. Delegated on the root so the handlers
    // survive HTMX / JS fragment swaps of #coda-list — freshly-injected
    // checkboxes are reached through the same delegated listener without
    // any re-binding. `change` fires from click and spacebar both.
    root.addEventListener('change', function(event) {
        var target = event.target;
        if (!target) return;
        if (target.id === 'coda-select-all') {
            var checked = !!target.checked;
            var rows = getRowChecks();
            for (var i = 0; i < rows.length; i++) rows[i].checked = checked;
            updateBulkBar();
            return;
        }
        if (target.classList && target.classList.contains('coda-row-check')) {
            updateBulkBar();
        }
    });
    // Re-evaluate the bulk bar after every HTMX swap of #coda-list —
    // checkbox state is ephemeral (the fresh fragment has no `checked`
    // attributes set), but the bar + select-all must reflect that too
    // instead of lingering with a stale count. Bound exactly ONCE for
    // the SPA lifetime via the module-level ``_globalListenersBound``
    // flag at the bottom of this function — see the rationale in
    // ``no-inline-javascript.mdc § Surviving HTMX swaps``: every visit
    // to this page would otherwise stack another listener and we'd
    // updateBulkBar / ensureCodaPolling N times per swap after N visits.
    bindGlobalListenersOnce();
    var clearBtn = document.getElementById('coda-bulk-clear');
    if (clearBtn) clearBtn.addEventListener('click', clearBulkSelection);
    var archiveBtn = document.getElementById('coda-bulk-archive');
    if (archiveBtn) archiveBtn.addEventListener('click', submitBulkArchive);

    // Open trigger — `data-coda-open` lives on the toolbar "Upload"
    // button (and could be reused on any other "open the upload modal"
    // affordance). Handing the click straight to the dynamic-modal
    // helper means the URL + title come from the trigger, exactly the
    // same shape as every other form modal in the office.
    root.addEventListener('click', function(event) {
        var opener = findClosest(event.target, '[data-coda-open]');
        if (!opener) return;
        event.preventDefault();
        var url = opener.getAttribute('data-url');
        if (!url) return;
        var title = opener.getAttribute('data-title') || '';
        var modalHelper = ensureCodaUploadModal();
        if (modalHelper) modalHelper.open(url, title);
    });

    // Filter panel: same collapse/reset pattern as the other office list
    // pages (see credit-notes.js / invoices.js). The HTMX form inside the
    // panel owns the actual request; this block is just UI plumbing.
    var filterPanel = document.getElementById('filter-panel');
    var filterToggleBtn = document.getElementById('toggle-filters-btn');
    var filterChevron = document.getElementById('filter-chevron');
    if (filterToggleBtn && filterPanel) {
        filterToggleBtn.addEventListener('click', function() {
            var isHidden = filterPanel.classList.toggle('hidden');
            if (filterChevron) filterChevron.classList.toggle('rotate-180', !isHidden);
        });
    }
    var filterResetBtn = document.getElementById('reset-filters-btn');
    var filterForm = document.getElementById('codaFilterForm');
    if (filterResetBtn && filterForm) {
        filterResetBtn.addEventListener('click', function() {
            var sel = filterForm.querySelector('select[name="archived"]');
            if (!sel) return;
            // "active" is the default; resetting means snapping back to it
            // and letting the HTMX `change` trigger re-fetch the list.
            if (sel.value === 'active') return;
            sel.value = 'active';
            // Sync any custom-select display that might be wrapping it.
            var wrapper = sel.closest('.custom-select-wrapper');
            var textEl = wrapper && wrapper.querySelector('.custom-select-text');
            if (textEl) {
                var opt = sel.options[sel.selectedIndex];
                if (opt) textEl.textContent = opt.text;
            }
            sel.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    // Re-arm the poll loop whenever htmx replaces #coda-list. The same
    // module-level guard handles binding here too — see comment above.
    bindGlobalListenersOnce();
}

// ---------------------------------------------------------------------
// One-time global listener wiring.
//
// Both ``htmx:afterSwap`` hooks below are bound to ``document.body``
// which survives every hx-boost swap. ``initCodaUpload`` re-runs on
// every navigation back to /office/billing/banks/, so without this
// idempotency guard each visit would stack another copy of the
// listener and ``updateBulkBar`` / ``ensureCodaPolling`` would fire
// N times per swap after N visits in the same session. Same anti-
// pattern that briefly broke the office filter button in
// ``customer-detail.js`` (cf. the long comment there). See also
// ``no-inline-javascript.mdc § Surviving HTMX swaps``.
// ---------------------------------------------------------------------

var _globalListenersBound = false;

function bindGlobalListenersOnce() {
    if (_globalListenersBound) return;
    _globalListenersBound = true;
    document.body.addEventListener('htmx:afterSwap', function() {
        updateBulkBar();
    });
    document.body.addEventListener('htmx:afterSwap', function(evt) {
        var target = evt && evt.detail && evt.detail.target;
        if (!target) return;
        ensureCodaPolling();
    });
}

export function autoInitCodaUpload() {
    initCodaUpload();
}
